import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Box, Button, Container, Typography } from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { pesquisaApi } from '../../services/apiPesquisa.servise';
import { useDispatch, useSelector } from 'react-redux';
import { saveChange, setArrayLength, setIdSection } from '../../store/reducers/dadosSegmentos';
import { authService } from '../../services/auth.service';
import { ModalComp } from '../Modal/Modal';
import { Loading } from '../Loading';
import { useNavigate } from 'react-router-dom';
import * as exifr from 'exifr';
import piexif from 'piexifjs';

const blobToDataURL = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const dataURLToBlob = async (dataURL) => {
  const res = await fetch(dataURL);
  return await res.blob();
};

const extractGpsData = async (file) => {
  try {
    const dataURL = await blobToDataURL(file);
    if (dataURL.startsWith('data:image/jpeg')) {
      const exifObj = piexif.load(dataURL);
      if (exifObj.GPS && Object.keys(exifObj.GPS).length > 0) {
        return { type: 'ifd', gpsIfd: exifObj.GPS };
      }
    }
  } catch {
  }

  try {
    const data = await exifr.parse(file, { gps: true });
    if (!data) return null;

    const { latitude, longitude, GPSAltitude, GPSAltitudeRef } = data;
    if (latitude == null || longitude == null) return null;

    return { type: 'decimal', latitude, longitude, GPSAltitude, GPSAltitudeRef };
  } catch {
    return null;
  }
};

const injectGpsIntoJpeg = async (blob, gpsData) => {
  if (!gpsData) return blob;

  const dataURL = await blobToDataURL(blob);

  let exifObj;
  try {
    exifObj = piexif.load(dataURL);
  } catch {
    exifObj = { '0th': {}, Exif: {}, GPS: {}, Interop: {}, '1st': {}, thumbnail: null };
  }

  if (gpsData.type === 'ifd') {
    exifObj.GPS = gpsData.gpsIfd;
  } else {
    const gps = {};
    const lat = Math.abs(gpsData.latitude);
    const lng = Math.abs(gpsData.longitude);

    gps[piexif.GPSIFD.GPSVersionID] = [2, 3, 0, 0];
    gps[piexif.GPSIFD.GPSLatitudeRef] = gpsData.latitude >= 0 ? 'N' : 'S';
    gps[piexif.GPSIFD.GPSLatitude] = piexif.GPSHelper.degToDmsRational(lat);
    gps[piexif.GPSIFD.GPSLongitudeRef] = gpsData.longitude >= 0 ? 'E' : 'W';
    gps[piexif.GPSIFD.GPSLongitude] = piexif.GPSHelper.degToDmsRational(lng);

    if (gpsData.GPSAltitude != null) {
      const alt = gpsData.GPSAltitude;
      if (Array.isArray(alt)) {
        gps[piexif.GPSIFD.GPSAltitude] = alt;
      } else {
        gps[piexif.GPSIFD.GPSAltitude] = [Math.round(Math.abs(alt) * 100), 100];
      }

      const altRef =
        gpsData.GPSAltitudeRef != null
          ? gpsData.GPSAltitudeRef
          : (Array.isArray(alt) ? alt[0] : alt) < 0
            ? 1
            : 0;
      gps[piexif.GPSIFD.GPSAltitudeRef] = altRef;
    }

    exifObj.GPS = gps;
  }

  const exifBytes = piexif.dump(exifObj);
  const newDataURL = piexif.insert(exifBytes, dataURL);

  return await dataURLToBlob(newDataURL);
};

const DragAndDrop = () => {
  const [incompleteRows, setImcompleteRows] = useState([]);
  const [openModalImagens, setOpenModalImagens] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [openBackdrop, setOpenBackdrop] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentStage, setCurrentStage] = useState('');
  const [acceptedUpload, setAcceptedUpload] = useState(false);
  const [filesUpload, setFilesUpload] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [debugInfo, setDebugInfo] = useState('');
  const workerRef = useRef(null);
  const jobIdRef = useRef(0);

  useEffect(() => {
    workerRef.current = new Worker("/imageWorker.js");

    return () => {
      workerRef.current?.terminate();
      workerRef.current = null;
    };
  }, []);

  const runWithConcurrency = async (tasks, concurrency, onProgress) => {
    let index = 0;
    let completed = 0;
    const results = [];

    const runner = async () => {
      while (index < tasks.length) {
        const current = index++;
        results[current] = await tasks[current]();
        completed++;
        onProgress?.(completed, tasks.length);
      }
    };

    const runners = Array.from(
      { length: Math.min(concurrency, tasks.length) },
      () => runner()
    );

    await Promise.all(runners);
    return results;
  };

  const { acceptedFiles, getRootProps, getInputProps } = useDropzone({
    accept: { 'image/*': [] },
    disabled: processing || acceptedUpload
  });

  const slices = useSelector((state) => state);
  const state = { ...slices.dadosSegmentos, ...slices.dadosHeader };
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const showConfirmationWarning = useCallback((event) => {
    if (processing) {
      const confirmationMessage = 'Tem certeza que deseja sair/atualizar esta página? Suas importações podem conter duplicadas se sair enquanto estiver fazendo upload das imagens';
      event.preventDefault();
      event.returnValue = confirmationMessage;
      return confirmationMessage;
    }
  }, [processing]);

  useEffect(() => {
    window.addEventListener('beforeunload', showConfirmationWarning);

    return () => {
      window.removeEventListener('beforeunload', showConfirmationWarning);
    };
  }, [showConfirmationWarning]);

  useEffect(() => {
    if (processing) {
      window.history.pushState(null, '', window.location.href);

      const handlePopState = (event) => {
        if (processing) {
          const confirm = window.confirm('Tem certeza que deseja sair/atualizar esta página? Suas importações podem conter duplicatas se sair enquanto estiver fazendo upload das imagens');
          if (confirm) {
            setProcessing(false);
            setOpenBackdrop(false);
            window.history.back();
          } else {
            event.preventDefault();
            window.history.pushState(null, '', window.location.href);
          }
        }
      };

      window.addEventListener('popstate', handlePopState);

      return () => {
        window.removeEventListener('popstate', handlePopState);
        if (window.history.state) {
          window.history.back();
        }
      };
    }
  }, [processing]);

  const compressInWorker = async (sectionIndex, files) => {
    const gpsDataList = await Promise.all(files.map((f) => extractGpsData(f)));

    const compressed = await new Promise((resolve, reject) => {
      const worker = workerRef.current;
      if (!worker) {
        resolve(files);
        return;
      }

      const jobId = ++jobIdRef.current;

      const onMessage = (e) => {
        const msg = e.data;
        if (!msg || msg.jobId !== jobId) return;

        worker.removeEventListener('message', onMessage);

        if (msg.ok) resolve(msg.files);
        else reject(new Error(msg.error));
      };

      worker.addEventListener('message', onMessage);

      worker.postMessage({
        jobId,
        sectionIndex,
        files,
        opts: {
          maxWidth: 1024,
          quality: 0.7,
          minSizeBytes: 500 * 1024,
          outputType: 'image/jpeg',
        },
      });
    });

    const withGps = await Promise.all(
      compressed.map(async (blob, i) => {
        const gpsData = gpsDataList[i];
        const originalName =
          files[i]?.name || blob.name || `section_${sectionIndex}_${i}.jpg`;

        if (!gpsData) {
          if (blob instanceof File) return blob;
          return new File([blob], originalName, { type: 'image/jpeg' });
        }

        try {
          const newBlob = await injectGpsIntoJpeg(blob, gpsData);
          return new File([newBlob], originalName, { type: 'image/jpeg' });
        } catch (err) {
          console.warn(`Falha ao reinjetar GPS em ${originalName}:`, err);
          return blob instanceof File
            ? blob
            : new File([blob], originalName, { type: 'image/jpeg' });
        }
      })
    );

    return withGps;
  };

  const sendSection = async (sectionGroup, index) => {
    try {
      const user = authService.getLoggedUser();
      if (!user) throw new Error("Usuário não autenticado");

      const compressedFiles = await compressInWorker(index, sectionGroup.files);

      const formData = new FormData();

      const imageRefName = sectionGroup.imageReference || `section_${index}`;
      formData.append("image_reference", compressedFiles[0]);

      formData.append("latitude", sectionGroup.rowData?.["Latitude"] || "0");
      formData.append("longitude", sectionGroup.rowData?.["Longitude"] || "0");
      const dataColeta = sectionGroup.rowData?.["Data de coleta de imagens em campo"];
      if (dataColeta !== undefined && dataColeta !== null && String(dataColeta).trim() !== "") {
        formData.append("image_collection_date", String(dataColeta).trim());
      }
      console.log(`Enviando seção ${index}:`, {
        imageRef: sectionGroup.imageReference,
        latitude: sectionGroup.rowData?.["Latitude"],
        longitude: sectionGroup.rowData?.["Longitude"],
        rowIndex: sectionGroup.rowIndex
      });

      formData.append("centre_0", compressedFiles[0]);
      formData.append("centre_20", compressedFiles[1]);
      formData.append("centre_40", compressedFiles[2]);
      formData.append("centre_60", compressedFiles[3]);
      formData.append("centre_80", compressedFiles[4]);

      formData.append("header", state.idHeader);
      formData.append("coder_name", user.username);

      const curvatura = sectionGroup.rowData?.["Curvatura"];
      const inclinacao = sectionGroup.rowData?.["Inclinação"];

      if (curvatura !== undefined && curvatura !== null && curvatura !== "") {
        formData.append("curvature", curvatura);
      }
      if (inclinacao !== undefined && inclinacao !== null && inclinacao !== "") {
        formData.append("inclination", inclinacao);
      }

      const resposta = await pesquisaApi.setSections(formData);

      dispatch(saveChange());
      dispatch(
        setIdSection({
          index: sectionGroup.rowIndex,
          item: {
            id_section: resposta.id_section,
            latitude: resposta.latitude,
            longitude: resposta.longitude,
          },
        })
      );

      return { success: true, sectionIndex: sectionGroup.rowIndex };
    } catch (error) {
      console.error(`Erro ao enviar seção ${index + 1}`, error);
      return { success: false, sectionIndex: index, error };
    }
  };

  const findMatchingImages = (files) => {
    if (!state.dadosTabelaDeReferencia || state.dadosTabelaDeReferencia.length === 0) {
      return { matchedGroups: [], incompleteRows: [], missingImages: [] };
    }

    const filesByName = {};
    files.forEach(file => {
      filesByName[file.name.toLowerCase()] = file;
    });

    const matchedGroups = [];
    const incompleteRows = [];
    const missingImages = [];

    state.dadosTabelaDeReferencia.forEach((row, rowIndex) => {
      const isLastRow = rowIndex === state.dadosTabelaDeReferencia.length - 1;

      const requiredImages = [
        { key: "centre - 0", value: row["centre - 0"] },
        { key: "centre - 20", value: row["centre - 20"] },
        { key: "centre - 40", value: row["centre - 40"] },
        { key: "centre - 60", value: row["centre - 60"] },
        { key: "centre - 80", value: row["centre - 80"] },
      ];

      const group = {
        files: [],
        rowData: row,
        rowIndex,
        imageReference: row["Image reference"],
      };

      let missingImageNames = [];

      requiredImages.forEach(({ key, value }) => {
        if (!value) {
          missingImageNames.push(`Coluna "${key}" vazia`);
          return;
        }

        const file = filesByName[value.trim().toLowerCase()];
        if (file) {
          group.files.push(file);
        } else {
          missingImageNames.push(value);
        }
      });

      const foundCount = group.files.length;

      if (isLastRow) {
        if (foundCount === 0) {
          return;
        }

        if (foundCount < 5) {
          const lastImage = group.files[foundCount - 1];
          while (group.files.length < 5) {
            group.files.push(lastImage);
          }
        }

        matchedGroups.push(group);
        return;
      }

      if (foundCount === 5) {
        matchedGroups.push(group);
      } else {
        incompleteRows.push({
          rowNumber: rowIndex + 2,
          missing: missingImageNames,
          found: foundCount,
          rowData: row,
        });
      }
    });

    return { matchedGroups, incompleteRows };
  };

  const processAllImages = async (files) => {
    if (processing) {
      return;
    }

    setProcessing(true);
    setOpenBackdrop(true);
    setCurrentStage('Iniciando análise...');
    setProgress(0);

    try {
      await new Promise(resolve => setTimeout(resolve, 100));

      setCurrentStage('Analisando imagens...');
      setProgress(5);

      const { matchedGroups, incompleteRows } = findMatchingImages(files);

      setDebugInfo(`Encontrados ${matchedGroups.length} grupos completos, ${incompleteRows.length} linhas incompletas`);

      matchedGroups.forEach((group, i) => {
        console.log(`Grupo ${i}:`, {
          rowIndex: group.rowIndex,
          imageRef: group.imageReference,
          lat: group.rowData?.["Latitude"],
          lng: group.rowData?.["Longitude"]
        });
      });

      if (incompleteRows.length > 0) {
        setImcompleteRows(incompleteRows);
        setOpenModalImagens(true);
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      if (matchedGroups.length === 0) {
        setCurrentStage('Nenhuma imagem completa encontrada');
        for (let p = progress; p <= 100; p += 10) {
          setProgress(p);
          await new Promise(resolve => setTimeout(resolve, 50));
        }
        setTimeout(() => {
          setOpenBackdrop(false);
          setProcessing(false);
        }, 1000);
        return;
      }

      const allFiles = matchedGroups.flatMap(group => group.files);
      const fileList = allFiles.map((file, index) => (
        <li key={`${file.name}-${index}`}>
          {file.name} - {(file.size / 1024).toFixed(1)} KB
        </li>
      ));
      setFilesUpload(fileList);

      dispatch(setArrayLength(matchedGroups.length));
      setProgress(10);

      setCurrentStage(`Enviando ${matchedGroups.length} grupos...`);
      setProgress(10);

      const tasks = matchedGroups.map((group, index) => {
        return async () => {
          setCurrentStage(`Enviando grupo ${index + 1}/${matchedGroups.length} (Linha ${group.rowIndex + 2})...`);
          return await sendSection(group, index);
        };
      });

      const results = await runWithConcurrency(
        tasks,
        1,
        (done, total) => {
          const base = 10;
          const range = 80;
          const progress = base + Math.round((done / total) * range);
          setProgress(progress);
        }
      );

      setProgress(95);
      setCurrentStage('Finalizando upload...');

      const failed = results.filter(r => !r.success);

      if (failed.length > 0) {
        console.warn(`${failed.length} grupos falharam no upload`);
        setCurrentStage(`Upload concluído com ${failed.length} falhas`);
      } else {
        setCurrentStage('Upload concluído com sucesso!');
      }

      setAcceptedUpload(true);

      for (let p = 95; p <= 100; p++) {
        setProgress(p);
        await new Promise(resolve => setTimeout(resolve, 30));
      }

      await new Promise(resolve => setTimeout(resolve, 800));

      setOpenBackdrop(false);
      setProcessing(false);

    } catch (error) {
      console.error('Erro geral no processamento:', error);
      setCurrentStage('Erro no processamento');
      setProgress(100);
      setTimeout(() => {
        setOpenBackdrop(false);
        setProcessing(false);
        setOpenModal(true);
      }, 1000);
    }
  };

  useEffect(() => {
    if (acceptedFiles.length > 0 && !processing && !acceptedUpload) {
      processAllImages(acceptedFiles);
    }
  }, [acceptedFiles]);

  return (
    <>
      {openBackdrop && (
        <Loading
          hasProgress={true}
          progress={progress}
          title={currentStage}
          subtitle={`${progress}% concluído`}
        />
      )}

      <Container sx={{ height: '80vh' }}>
        {openModal && (
          <ModalComp
            open={openModal}
            title={"Erro inesperado!"}
            subtitle={"Ocorreu um erro inesperado, por favor tente novamente."}
          >
            <Button
              onClick={() => {
                setOpenModal(false);
                if (processing) {
                  const confirm = window.confirm('Tem certeza que deseja sair/atualizar esta página? Suas importações podem conter duplicatas se sair enquanto estiver fazendo upload das imagens');
                  if (confirm) {
                    setProcessing(false);
                    navigate("/");
                  }
                } else {
                  navigate("/");
                }
              }}
            >
              Ok
            </Button>
          </ModalComp>
        )}

        {openModalImagens && (
          <ModalComp
            open={openModalImagens}
            title={"Algumas linhas têm problemas"}
            subtitle={"Estas linhas contêm imagens ausentes ou duplicadas. Continuando com as linhas completas..."}
            width="md"
          >
            <Box sx={{ maxHeight: '300px', overflowY: 'auto', mb: 2 }}>
              {incompleteRows.map((rowInfo, i) => (
                <Box key={i} sx={{
                  mb: 1,
                  p: 1,
                  bgcolor: '#fff3cd',
                  borderRadius: 1,
                  border: '1px solid #ffeaa7'
                }}>
                  <Typography variant="body1" fontWeight="bold">
                    Linha {rowInfo.rowNumber}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Encontradas: {rowInfo.found}/5 imagens
                  </Typography>
                  {rowInfo.missing && rowInfo.missing.length > 0 && (
                    <Typography variant="caption" color="error">
                      Faltando: {rowInfo.missing.slice(0, 3).join(', ')}
                      {rowInfo.missing.length > 3 && `... (+${rowInfo.missing.length - 3} mais)`}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => {
                  setOpenModalImagens(false);
                  setProcessing(false);
                  setOpenBackdrop(false);
                  if (processing) {
                    const confirm = window.confirm('Tem certeza que deseja recarregar a página? Suas importações podem conter duplicatas se sair enquanto estiver fazendo upload das imagens');
                    if (confirm) {
                      window.location.reload();
                    }
                  } else {
                    window.location.reload();
                  }
                }}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  setOpenModalImagens(false);
                }}
              >
                Continuar
              </Button>
            </Box>
          </ModalComp>
        )}

        <Box
          {...getRootProps()}
          sx={{
            backgroundColor: '#eeeeee',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '30vh',
            border: acceptedUpload ? 'dashed 4px rgba(0, 255, 0, 1)' :
              processing ? 'dashed 4px rgba(255, 165, 0, 1)' :
                'dashed 4px rgba(169, 169, 169, 1)',
            cursor: processing || acceptedUpload ? 'default' : 'pointer',
            mt: '14px',
            transition: 'border-color 0.3s',
            '&:hover': {
              borderColor: !processing && !acceptedUpload ? 'rgba(0, 0, 0, 0.3)' : undefined
            }
          }}
        >
          <input {...getInputProps()} />

          {filesUpload.length === 0 && !processing && !acceptedUpload && (
            <Box textAlign="center" p={3}>
              <Typography variant="h6" gutterBottom>
                Arraste e solte as imagens aqui
              </Typography>
              <Typography variant="body2" color="textSecondary">
                ou clique para selecionar as imagens
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 2, color: '#666' }}>
                {debugInfo || 'As imagens serão automaticamente comprimidas e enviadas'}
              </Typography>
            </Box>
          )}

          {processing && (
            <Box textAlign="center" p={3}>
              <Typography variant="h6" gutterBottom>
                {filesUpload.length > 0 ? `Processando ${filesUpload.length} imagens...` : 'Processando...'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {currentStage}
              </Typography>
            </Box>
          )}

          {acceptedUpload && !processing && (
            <Box textAlign="center" p={3}>
              <Typography variant="h6" gutterBottom sx={{ color: 'success.main' }}>
                ✓ Upload concluído!
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Pronto para iniciar a codificação
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1, color: '#666' }}>
                {debugInfo}
              </Typography>
            </Box>
          )}
        </Box>

        {filesUpload.length > 0 && (
          <>
            <Typography variant="h5" sx={{ mt: 3, mb: 2 }}>
              {acceptedUpload ? 'Imagens enviadas:' : 'Imagens sendo processadas:'}
            </Typography>
            <Box sx={{
              height: '40vh',
              overflowY: 'auto',
              p: 2,
              bgcolor: 'background.paper',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider'
            }}>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                {filesUpload}
              </ul>
            </Box>
          </>
        )}
      </Container>
    </>
  );
};

export default DragAndDrop;